import { useAuth } from '@/context/AuthContext';
import { MetricCard } from '@/components/common/MetricCard';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Package,
  Truck,
  AlertTriangle,
  TrendingUp,
  Clock,
  DollarSign,
  Activity,
  CheckCircle,
} from 'lucide-react';

export function Dashboard() {
  const { user } = useAuth();

  return (
    <div className="space-y-6 animate-fade-in-up">
      {/* Welcome header */}
      <div>
        <h1 className="text-h2 font-semibold text-foreground">
          Welcome back, {user?.fullName?.split(' ')[0] || 'User'}
        </h1>
        <p className="mt-1 text-muted-foreground">
          Here's an overview of your supply chain operations
        </p>
      </div>

      {/* KPI Grid - LognAI Pattern */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        <MetricCard
          label="Total Inventory Items"
          value="12,847"
          trend={{ direction: 'up', value: '12%', text: 'vs last month' }}
          icon={<Package className="h-5 w-5" />}
        />
        <MetricCard
          label="Active Shipments"
          value="156"
          trend={{ direction: 'up', value: '8%', text: 'vs last month' }}
          icon={<Truck className="h-5 w-5" />}
        />
        <MetricCard
          label="Low Stock Alerts"
          value="23"
          trend={{ direction: 'down', value: '5%', text: 'vs last month' }}
          icon={<AlertTriangle className="h-5 w-5" />}
        />
        <MetricCard
          label="On-Time Delivery"
          value="94.2%"
          trend={{ direction: 'up', value: '2.1%', text: 'vs last month' }}
          icon={<TrendingUp className="h-5 w-5" />}
        />
      </div>

      {/* Secondary KPI Grid - Grouped with dashed border */}
      <div className="rounded-xl border border-dashed border-gray-300 p-4 dark:border-slate-700">
        <h2 className="mb-4 text-sm font-medium text-muted-foreground">Performance Metrics</h2>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          <MetricCard
            label="Avg. Lead Time"
            value="4.2 days"
            trend={{ direction: 'down', value: '0.5', text: 'days faster' }}
            icon={<Clock className="h-5 w-5" />}
          />
          <MetricCard
            label="Inventory Turnover"
            value="8.4x"
            trend={{ direction: 'up', value: '0.6x', text: 'improvement' }}
            icon={<Activity className="h-5 w-5" />}
          />
          <MetricCard
            label="Cost Savings"
            value="$124K"
            trend={{ direction: 'up', value: '18%', text: 'vs target' }}
            icon={<DollarSign className="h-5 w-5" />}
          />
          <MetricCard
            label="Order Accuracy"
            value="99.2%"
            trend={{ direction: 'up', value: '0.3%', text: 'improvement' }}
            icon={<CheckCircle className="h-5 w-5" />}
          />
        </div>
      </div>

      {/* Main content grid */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Recent Activity */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Activity</CardTitle>
            <CardDescription>Latest updates across your supply chain</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentActivity.map((activity, index) => (
                <div key={index} className="flex items-start gap-4">
                  <div
                    className={`mt-0.5 rounded-full p-2 ${
                      activity.type === 'success'
                        ? 'bg-success/10 text-success'
                        : activity.type === 'warning'
                          ? 'bg-warning/10 text-warning'
                          : activity.type === 'error'
                            ? 'bg-destructive/10 text-destructive'
                            : 'bg-primary/10 text-primary'
                    }`}
                  >
                    {activity.icon}
                  </div>
                  <div className="flex-1 space-y-1">
                    <p className="text-sm font-medium">{activity.title}</p>
                    <p className="text-xs text-muted-foreground">{activity.description}</p>
                  </div>
                  <span className="text-xs text-muted-foreground">{activity.time}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Pending Approvals */}
        <Card>
          <CardHeader>
            <CardTitle>Pending Approvals</CardTitle>
            <CardDescription>Items requiring your attention</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {pendingApprovals.map((approval, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between rounded-lg border p-3"
                >
                  <div className="flex items-center gap-3">
                    <div className="rounded-full bg-primary/10 p-2 text-primary">
                      {approval.icon}
                    </div>
                    <div>
                      <p className="text-sm font-medium">{approval.title}</p>
                      <p className="text-xs text-muted-foreground">{approval.subtitle}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold">{approval.value}</p>
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                        approval.priority === 'high'
                          ? 'bg-destructive/10 text-destructive'
                          : approval.priority === 'medium'
                            ? 'bg-warning/10 text-warning'
                            : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {approval.priority}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

const recentActivity = [
  {
    type: 'success',
    icon: <CheckCircle className="h-4 w-4" />,
    title: 'Shipment SHP-2024-001 delivered',
    description: 'Delivered to Houston Hub',
    time: '2m ago',
  },
  {
    type: 'warning',
    icon: <AlertTriangle className="h-4 w-4" />,
    title: 'Low stock alert: Drill Bits',
    description: 'Current stock: 45 units (min: 100)',
    time: '15m ago',
  },
  {
    type: 'info',
    icon: <Truck className="h-4 w-4" />,
    title: 'New shipment created',
    description: 'SHP-2024-002 to Midland Site',
    time: '1h ago',
  },
  {
    type: 'success',
    icon: <Package className="h-4 w-4" />,
    title: 'PO-2024-089 received',
    description: '250 items added to inventory',
    time: '2h ago',
  },
];

const pendingApprovals = [
  {
    icon: <Package className="h-4 w-4" />,
    title: 'Purchase Order',
    subtitle: 'PO-2024-092',
    value: '$45,000',
    priority: 'high',
  },
  {
    icon: <Truck className="h-4 w-4" />,
    title: 'Shipment Request',
    subtitle: 'SHP-2024-003',
    value: '120 items',
    priority: 'medium',
  },
  {
    icon: <DollarSign className="h-4 w-4" />,
    title: 'Budget Increase',
    subtitle: 'Q1 2024 Inventory',
    value: '$12,500',
    priority: 'low',
  },
];
